import { useEffect, useMemo, useState, type CSSProperties, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  ASSET_CONDITIONS,
  assetsApi,
  type AssetCondition,
  type AssetDetail,
  type AssetRequest,
} from "../api/assets";
import { locationsApi } from "../api/locations";
import type { ApiError } from "../api/client";
import { detailsMap, formatApiError } from "../lib/apiError";
import { flattenLocations, type FlatLocation } from "../lib/locationsFlat";
import { assetTypesApi, type AttributeDefView, type AttributeValue } from "../api/catalog";
import { AssetOriginFields } from "./AssetOriginFields";
import { AttributeValuesForm, compactAttributeValues, missingRequiredAttributes } from "./AttributeValuesForm";
import { AssetTypeCombobox, type AssetTypeRef } from "./AssetTypeCombobox";
import { conditionLabel } from "./ConditionChip";
import { LocationDestinationFields, missingReturnDate, returnDateFor } from "./LocationDestinationFields";
import { Modal } from "./Modal";
import { Select, type SelectOption } from "./Select";

type Translate = (key: string, fallback: string, opts?: Record<string, unknown>) => string;

type Mode = { kind: "create"; defaultTypeId?: AssetTypeRef | null } | { kind: "edit"; asset: AssetDetail };

/**
 * Create (POST — always unmounted, on a physical location) and edit (PUT —
 * identity only: tag, serial, type) of an asset. Actual values and piece
 * data are edited inline on the karton with the same PUT.
 */
export function AssetFormModal({ mode, onClose, onSaved }: { mode: Mode; onClose: () => void; onSaved: (saved: AssetDetail) => void }) {
  const { t } = useTranslation();
  const editing = mode.kind === "edit" ? mode.asset : null;
  const [tag, setTag] = useState(editing?.tag ?? "");
  const [serialNo, setSerialNo] = useState(editing?.serialNo ?? "");
  const [type, setType] = useState<AssetTypeRef | null>(
    editing ? { id: editing.typeId, code: editing.typeCode, name: editing.typeName } : mode.kind === "create" ? (mode.defaultTypeId ?? null) : null,
  );
  const [manufacturerId, setManufacturerId] = useState("");
  const [manufacturerCode, setManufacturerCode] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [isCustomMade, setIsCustomMade] = useState(false);
  const [condition, setCondition] = useState<AssetCondition>("ISPRAVAN");
  const [flat, setFlat] = useState<FlatLocation[] | null>(null);
  const [locationId, setLocationId] = useState("");
  const [locationNote, setLocationNote] = useState("");
  const [expectedReturnAt, setExpectedReturnAt] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Attributes of the picked type open right in the form (Strahinja, 2026-09-10); required ones gate "Kreiraj".
  const [defs, setDefs] = useState<AttributeDefView[]>([]);
  const [values, setValues] = useState<Record<string, AttributeValue>>({});

  useEffect(() => {
    if (editing || !type) { setDefs([]); setValues({}); return; }
    let cancelled = false;
    assetTypesApi.get(type.id)
      .then((d) => { if (!cancelled) { setDefs(d.attributeDefs); setValues({}); } })
      .catch((err: ApiError) => { if (!cancelled) { setDefs([]); setError(err.message); } });
    return () => { cancelled = true; };
  }, [editing, type]);
  const missingRequired = useMemo(() => (editing ? [] : missingRequiredAttributes(defs, values)), [editing, defs, values]);

  useEffect(() => {
    if (editing) return;
    let cancelled = false;
    locationsApi.tree()
      .then((tree) => { if (!cancelled) setFlat(flattenLocations(tree)); })
      .catch((err: ApiError) => { if (!cancelled) { setFlat([]); setError(err.message); } });
    return () => { cancelled = true; };
  }, [editing]);

  const conditionOptions = useMemo<SelectOption<AssetCondition>[]>(
    () => ASSET_CONDITIONS.map((c) => ({ value: c, label: conditionLabel(t, c) })),
    [t],
  );

  const mounted = editing?.usage === "MONTIRAN";
  const typeChanged = !!editing && type?.id !== editing.typeId;
  const canSubmit = !!tag.trim() && !!type && (editing ? !(mounted && typeChanged) : !!locationId && !missingReturnDate(flat, locationId, expectedReturnAt) && missingRequired.length === 0) && !submitting;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit || !type) return;
    setError(null);
    setSubmitting(true);
    try {
      if (editing) {
        onSaved(await assetsApi.update(editing.id, {
          tag: tag.trim(),
          serialNo: serialNo.trim() || null,
          typeId: type.id,
          // Piece data (manufacturer, note) is edited on the karton — resend as is.
          manufacturerId: editing.manufacturerId,
          manufacturerCode: editing.manufacturerCode,
          supplierId: editing.supplierId,
          isCustomMade: editing.isCustomMade,
          // Type change invalidates the stored values (they belong to the old defs).
          actualValues: typeChanged ? {} : compactAttributeValues(editing.actualValues),
          note: editing.note,
        }));
      } else {
        const body: AssetRequest = {
          tag: tag.trim(),
          serialNo: serialNo.trim() || null,
          typeId: type.id,
          manufacturerId: manufacturerId || null,
          manufacturerCode: manufacturerCode.trim() || null,
          supplierId: supplierId || null,
          isCustomMade,
          actualValues: compactAttributeValues(values),
          condition,
          locationId,
          locationNote: locationNote.trim() || null,
          expectedReturnAt: returnDateFor(flat, locationId, expectedReturnAt),
          note: note.trim() || null,
        };
        onSaved(await assetsApi.create(body));
      }
    } catch (err) {
      setError(translateAssetError(t, err as ApiError));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={editing ? t("assets.modal.editTitle", "Izmena asseta") : t("assets.modal.createTitle", "Novi asset")}
      subtitle={editing ? undefined : t("assets.modal.createSub", "Asset se kreira nemontiran, na fizičkoj lokaciji. Montaža je zaseban korak.")}
      onClose={onClose}
      width={620}
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div className="form-grid">
          <label className="field">
            <span>{t("assets.field.tag", "Oznaka")} <span className="req-mark">*</span></span>
            <input
              className="input"
              value={tag}
              onChange={(e) => setTag(e.target.value.toUpperCase())}
              required
              maxLength={40}
              placeholder="EM-0347"
              autoCapitalize="characters"
              spellCheck={false}
              style={{ fontFamily: "var(--mono)" }}
            />
          </label>
          <label className="field">
            <span>{t("assets.field.serialNo", "Serijski broj")}</span>
            <input className="input" value={serialNo} onChange={(e) => setSerialNo(e.target.value)} maxLength={100} style={{ fontFamily: "var(--mono)" }} />
          </label>
        </div>
        <label className="field">
          <span>{t("assets.field.type", "Tip")} <span className="req-mark">*</span></span>
          <AssetTypeCombobox value={type} onChange={setType} disabled={mounted} />
          {mounted && <span className="muted" style={{ fontSize: 12 }}>{t("assets.modal.typeLockedMounted", "Tip se menja samo dok asset nije montiran.")}</span>}
        </label>
        {!editing && (
          <>
            {defs.length > 0 && (
              <div className="field">
                <span>{t("assets.section.typeAttributes", "Atributi tipa")}{missingRequired.length > 0 && <span className="req-mark"> *</span>}</span>
                <AttributeValuesForm defs={defs} values={values} disabled={false} onChange={setValues} />
                {missingRequired.length > 0 && (
                  <span className="muted" style={{ fontSize: 12 }}>
                    {t("assets.requiredMissing", "Obavezno: {{list}}", { list: missingRequired.map((d) => d.label).join(", ") })}
                  </span>
                )}
              </div>
            )}
            <AssetOriginFields
              value={{ manufacturerId, manufacturerCode, supplierId, isCustomMade }}
              onChange={(o) => { setManufacturerId(o.manufacturerId); setManufacturerCode(o.manufacturerCode); setSupplierId(o.supplierId); setIsCustomMade(o.isCustomMade); }}
            />
            <div className="form-grid">
              <label className="field">
                <span>{t("assets.field.condition", "Stanje")}</span>
                <Select<AssetCondition> value={condition} options={conditionOptions} onChange={setCondition} ariaLabel={t("assets.field.condition", "Stanje")} />
              </label>
            </div>
            <LocationDestinationFields
              flat={flat}
              locationId={locationId}
              onLocationChange={setLocationId}
              locationNote={locationNote}
              onLocationNoteChange={setLocationNote}
              expectedReturnAt={expectedReturnAt}
              onExpectedReturnAtChange={setExpectedReturnAt}
            />
            <label className="field">
              <span>{t("assets.field.note", "Napomena")}</span>
              <textarea className="input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} style={textarea} />
            </label>
          </>
        )}
        {error && <div style={errorBox} role="alert">{error}</div>}
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>{t("action.cancel", "Otkaži")}</button>
          <button type="submit" className="btn primary" disabled={!canSubmit}>
            {submitting ? t("action.saving", "Čuvanje…") : editing ? t("action.save", "Sačuvaj") : t("action.create", "Kreiraj")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function translateAssetError(t: Translate, ae: ApiError): string {
  if (ae.code === "REQUIRED_ATTRIBUTE") {
    const key = String((ae.details as { key?: string } | undefined)?.key ?? "");
    return t("assets.error.requiredAttribute", "Obavezan atribut nije unet: {{key}}", { key });
  }
  switch (ae.code) {
    case "TAG_TAKEN": return t("assets.error.tagTaken", "Oznaka je već zauzeta.");
    case "MOUNTED": return t("assets.error.mounted", "Asset je montiran — ova izmena nije dozvoljena dok je montiran.");
    case "UNKNOWN_ATTRIBUTE": return t("assets.error.unknownAttribute", "Nepoznat atribut: {{key}}", { key: detailsMap(ae)?.key ?? "" });
    case "INVALID_ATTRIBUTE_VALUE": return t("assets.error.invalidAttributeValue", "Neispravna vrednost atributa: {{key}}", { key: detailsMap(ae)?.key ?? "" });
    default: return formatApiError(ae);
  }
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
const textarea: CSSProperties = { resize: "vertical", fontFamily: "inherit", width: "100%" };
